import {Link, useLocation, useNavigate} from 'react-router-dom';
import {ChevronLeft, ChevronRight, Loader2} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import type {RouterOutput} from '@common-web/lib/trpc';
import {formatDate} from '@internal/pages/group-pipeline/components/pipelineDisplay';
import {StatusIndicator} from '@internal/pages/group-pipeline/components/StatusIndicator';
import {TenantTag} from '@internal/pages/group-pipeline/components/TenantTag';
import {VbaStatusIndicator} from '@internal/pages/group-pipeline/components/VbaStatusIndicator';
import {PhaseBadge} from '@internal/pages/group-pipeline/components/PhaseBadge';
import type {ListColumnKey} from '@common/groupPipeline/viewConfig';

type ListData = RouterOutput['internal']['groupPipeline']['list'];

export const PAGE_SIZE_OPTIONS = [25, 50, 100];

export function GroupPipelineList({
  columns,
  data,
  isLoading,
  isFetching,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: {
  columns: ListColumnKey[];
  data: ListData | undefined;
  isLoading: boolean;
  isFetching: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const show = (key: ListColumnKey) => columns.includes(key);
  const columnCount = columns.length + 1;

  return (
    <div className='overflow-hidden rounded-lg border bg-white'>
      <div className='overflow-x-auto'>
        <table className='w-full min-w-[900px] text-sm'>
          <thead className='border-b bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground'>
            <tr>
              <th className='px-6 py-3 font-semibold'>Group</th>
              {show('phase') && <th className='px-4 py-3 font-semibold'>Phase</th>}
              {show('currentStep') && <th className='px-4 py-3 font-semibold'>Current step</th>}
              {show('status') && <th className='px-4 py-3 font-semibold'>Status</th>}
              {show('members') && <th className='px-4 py-3 text-right font-semibold'>Members</th>}
              {show('effective') && (
                <th className='px-6 py-3 text-right font-semibold'>Effective</th>
              )}
            </tr>
          </thead>
          <tbody className={isFetching ? 'opacity-60 transition-opacity' : undefined}>
            {isLoading ? (
              <tr>
                <td colSpan={columnCount} className='py-16 text-center'>
                  <Loader2 className='mx-auto h-6 w-6 animate-spin text-muted-foreground' />
                </td>
              </tr>
            ) : !data?.rows.length ? (
              <tr>
                <td
                  colSpan={columnCount}
                  className='py-16 text-center text-sm text-muted-foreground'
                >
                  No groups match these filters.
                </td>
              </tr>
            ) : (
              data.rows.map((row) => (
                <tr
                  key={row.vbaGroupId}
                  onClick={() =>
                    navigate(`/group-pipeline/${row.vbaGroupId}`, {
                      state: {from: location.pathname + location.search},
                    })
                  }
                  className='cursor-pointer border-b last:border-0 hover:bg-muted/30'
                >
                  <td className='px-6 py-3'>
                    <div className='flex flex-wrap items-center gap-x-2 gap-y-1'>
                      {/* The link makes rows reachable by keyboard; the row click is for the mouse. */}
                      <Link
                        to={`/group-pipeline/${row.vbaGroupId}`}
                        state={{from: location.pathname + location.search}}
                        onClick={(e) => e.stopPropagation()}
                        className='font-medium hover:underline focus-visible:underline'
                      >
                        {row.name}
                      </Link>
                      <TenantTag shortName={row.tenantName} />
                      <VbaStatusIndicator status={row.vbaStatus} quiet />
                    </div>
                  </td>
                  {show('phase') && (
                    <td className='px-4 py-3'>
                      <PhaseBadge phase={row.phase} />
                    </td>
                  )}
                  {show('currentStep') && (
                    <td className='px-4 py-3'>{row.currentStep?.label ?? '—'}</td>
                  )}
                  {show('status') && (
                    <td className='px-4 py-3'>
                      <StatusIndicator status={row.status} context={row.statusContext} />
                    </td>
                  )}
                  {show('members') && (
                    <td className='whitespace-nowrap px-4 py-3 text-right'>
                      {row.members.toLocaleString()}
                    </td>
                  )}
                  {show('effective') && (
                    <td className='whitespace-nowrap px-6 py-3 text-right'>
                      {formatDate(row.effectiveDate)}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className='flex items-center justify-between border-t px-6 py-3 text-sm text-muted-foreground'>
        <span>
          {total === 0
            ? 'No results'
            : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total.toLocaleString()}`}
        </span>
        <div className='flex items-center gap-3'>
          <div className='flex items-center gap-2'>
            <span>Rows per page</span>
            <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
              <SelectTrigger className='h-8 w-[70px]'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <span>
            Page {page} of {totalPages}
          </span>
          <Button
            variant='outline'
            size='sm'
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            aria-label='Previous page'
          >
            <ChevronLeft className='h-4 w-4' />
          </Button>
          <Button
            variant='outline'
            size='sm'
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            aria-label='Next page'
          >
            <ChevronRight className='h-4 w-4' />
          </Button>
        </div>
      </div>
    </div>
  );
}
